"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "How can revenue management improve your hotel's profitability?",
  answer: "Implement real-time pricing and inventory adjustments based on market demand, competitor rates, and historical data to ensure the right room is sold to the right guest at the right price, maximizing your RevPAR."
}, {
  question: "What are the primary benefits of OTA optimization?",
  answer: "Start by ensuring your property ranks higher on platforms like Booking.com and Expedia through high-quality imagery and rate parity, which increases conversion rates and reduces reliance on heavy discounting."
}, {
  question: "Why should you invest in a robust hotel technology stack?",
  answer: "Implement integrated systems including Property Management Systems (PMS), Channel Managers, and Booking Engines to automate operations and provide accurate data for informed decision-making."
}, {
  question: "What components are included in a professional revenue audit?",
  answer: "Start by evaluating your current pricing strategy, distribution channel performance, market positioning, and tech stack efficiency to identify missed revenue opportunities through a detailed actionable report."
}, {
  question: "When can you expect to see results from revenue management?",
  answer: "Implement consistent strategies to see immediate improvements in OTA visibility within weeks, with significant growth in RevPAR and direct bookings typically occurring within 3 to 6 months."
}, {
  question: "Why choose HotelRevBaBa for your revenue management?",
  answer: "Start by leveraging our deep hospitality expertise combined with data-driven strategies specifically tailored for independent hotels and boutique properties, providing hands-on, personalized solutions."
}, {
  question: "How can you shift bookings to your direct website?",
  answer: "Implement strategies to shift bookings from high-commission OTAs to your direct website by optimizing your booking engine, improving website conversion, and leveraging digital marketing to capture high-value guests."
}, {
  question: "Where do you provide revenue management services?",
  answer: "Implement our scalable and effective revenue management strategies for hotels, resorts, and boutique properties across the globe, building on our deep expertise in the hospitality market."
}];
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  return <section data-section-id="2463" id="faq" className="bg-slate-50 text-slate-900 py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-default text-xs tracking-widest uppercase text-blue-600 mb-8">
                COMMON INQUIRIES / 03
              </div>

              <h2 className="font-default font-bold text-slate-900 mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Channel
                <br />
                Management &
                <br />
                Distribution FAQs.
              </h2>

              <p className="font-default text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "hsl(222 47% 20%)"
            }}>
                Effective multi-channel distribution is essential for maximizing hotel visibility and driving direct bookings. Explore our FAQs to understand how to optimize your channel management strategy for better performance.
              </p>

              <Button variant="outline" size="lg" className="font-default font-medium tracking-wider uppercase text-xs border-slate-900 text-slate-900 bg-transparent hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-300 px-8 py-6 h-auto rounded-lg">
                Contact Our Experts
              </Button>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 transition-all duration-300 group rounded-lg" style={{
              backgroundColor: "white",
              border: "1px solid hsl(215 16% 88%)",
              boxShadow: "0 4px 12px hsla(222 47% 7% / 0.05)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:[border-color:hsl(215 16% 88%)]">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-blue-600 shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-slate-900 leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-blue-600 shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-default text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "hsl(222 47% 20%)"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
            <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonLd)
          }} />
          </div>
        </div>
      </div>
    </section>;
}